import React from 'react';
import { Link } from 'react-router-dom';
import {
  PenTool, LayoutTemplate, Palette, Download, Users, Sparkles, Store, ArrowRight,
} from 'lucide-react';
import Header from '../components/Layout/Header';
import Footer from '../components/Layout/Footer';
import RichText from '../components/content/RichText';
import usePageMeta from '../lib/usePageMeta';
import { ABOUT } from '../content/sitePages';

const ICONS = [PenTool, LayoutTemplate, Palette, Download, Users, Sparkles, Store];

export default function AboutPage() {
  usePageMeta(ABOUT.title, ABOUT.description, { path: '/about' });

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      <main className="flex-1">
        <section className="relative overflow-hidden bg-gradient-to-br from-violet-600 via-purple-600 to-fuchsia-600 text-white">
          <div className="absolute -top-24 -left-24 w-96 h-96 bg-white/5 rounded-full blur-3xl pointer-events-none" aria-hidden="true" />
          <div className="relative max-w-4xl mx-auto px-4 sm:px-6 py-16 sm:py-20 text-center">
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">{ABOUT.title}</h1>
            {ABOUT.intro.map((p) => (
              <p key={p} className="mt-5 text-lg sm:text-xl text-white/85 max-w-2xl mx-auto leading-relaxed"><RichText text={p} /></p>
            ))}
          </div>
        </section>

        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center">What you can do with iVisora</h2>
          {/* flex-wrap + centred so an incomplete last row sits in the middle */}
          <ul className="mt-10 flex flex-wrap justify-center gap-5">
            {ABOUT.features.map((f, i) => {
              const Icon = ICONS[i % ICONS.length];
              return (
                <li key={f.title} className="w-full sm:w-[calc(50%-0.625rem)] lg:w-[calc(33.333%-0.84rem)] bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                  <div className="w-11 h-11 rounded-xl bg-purple-50 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-purple-600" aria-hidden="true" />
                  </div>
                  <h3 className="font-semibold text-gray-900">{f.title}</h3>
                  <p className="mt-2 text-sm text-gray-600 leading-relaxed">{f.body}</p>
                </li>
              );
            })}
          </ul>
        </section>

        <section className="bg-white border-y">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14 space-y-10">
            {ABOUT.sections.map((s) => (
              <div key={s.id}>
                <h2 id={s.id} className="text-xl font-bold text-gray-900">{s.heading}</h2>
                {s.blocks.map((b) => <p key={b.p} className="mt-3 text-gray-700 leading-relaxed"><RichText text={b.p} /></p>)}
              </div>
            ))}
          </div>
        </section>

        <section className="max-w-3xl mx-auto px-4 sm:px-6 py-14 sm:py-20 text-center">
          <h2 className="text-2xl font-bold text-gray-900">Ready to make something?</h2>
          <p className="mt-3 text-gray-600">Start from a template, or tell us what you need.</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/templates" className="h-12 px-6 rounded-xl bg-purple-600 hover:bg-purple-700 text-white font-semibold inline-flex items-center justify-center gap-2 transition-colors">
              Browse templates <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
            <Link to="/contact" className="h-12 px-6 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-gray-800 font-semibold inline-flex items-center justify-center transition-colors">
              Contact us
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
